import { SupabaseClient } from '@supabase/supabase-js';
import { supabaseClient } from './supabaseClient';
import { s3StorageService } from './s3StorageService';

class StorageService {
    private supabase: SupabaseClient;
    private headCache = new Map<string, { exists: boolean; timestamp: number }>();
    private readonly CACHE_TTL = 60 * 1000; // 1 minute
    private bucketInitialized = false;

    constructor() {
        this.supabase = supabaseClient;
        this.ensureBucketExists();
    }

    private async ensureBucketExists(): Promise<void> {
        if (this.bucketInitialized) return;
        
        try {
            // Test bucket access directly with a minimal operation
            // Instead of listing buckets (which may have permission issues), 
            // try to get the public URL for a test path - this is less privileged
            const testResult = this.supabase.storage
                .from('cached-images')
                .getPublicUrl('test');
            
            if (testResult.data?.publicUrl) {
                // If we get a public URL back, the bucket likely exists
                console.log('[StorageService] Bucket "cached-images" access verified');
                this.bucketInitialized = true;
                return;
            }
            
            // If no public URL, bucket might not exist
            console.warn('[StorageService] Bucket "cached-images" may not exist. Please create it manually.');
            console.warn('[StorageService] Run: npm run setup:storage or create via Supabase Dashboard');
            console.warn('[StorageService] Continuing anyway - uploads will provide clearer errors if bucket missing');
            this.bucketInitialized = true;
            
        } catch (error) {
            // Even if the test fails, continue - actual operations will provide better error messages
            console.warn('[StorageService] Could not verify bucket access, continuing anyway');
            console.debug('[StorageService] Bucket test error:', error);
            this.bucketInitialized = true;
        }
    }

    async uploadImageToStorage(file: Blob, fileName: string): Promise<{ publicUrl: string } | null> {
        try {
            // Ensure bucket exists
            await this.ensureBucketExists();
            
            // Try S3 first if available
            if (s3StorageService.isAvailable()) {
                const s3Url = await s3StorageService.uploadFile(fileName, file, 'image/png');
                if (s3Url) {
                    return { publicUrl: s3Url };
                }
            }

            // Clean the filename to avoid path issues
            const cleanFileName = fileName.replace(/^generated\//, '');
            
            // Fallback to regular Supabase storage
            const { data, error } = await this.supabase.storage
                .from('cached-images')
                .upload(`images/${cleanFileName}`, file, { 
                    contentType: 'image/png', 
                    upsert: true,
                    cacheControl: '3600'
                });
                
            if (error) {
                console.error('[StorageService] Upload error:', error);
                // If it's an RLS error, try with a simpler approach
                if (error.message?.includes('row-level security')) {
                    console.log('[StorageService] Retrying with public upload...');
                    // Return the direct URL from Pollinations as fallback
                    return null;
                }
                return null;
            }
            
            const { data: publicUrlData } = this.supabase.storage
                .from('cached-images')
                .getPublicUrl(data.path);
                
            console.log('[StorageService] Upload successful:', publicUrlData.publicUrl);
            return { publicUrl: publicUrlData.publicUrl };
        } catch (error) {
            console.error('[StorageService] Upload failed:', error);
            return null;
        }
    }

    async getCachedImage(cacheKey: string): Promise<string | null> {
        try {
            // Ensure bucket exists before trying to access it
            await this.ensureBucketExists();
            const cached = this.headCache.get(cacheKey);
            if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
                const encodedCacheKey = encodeURIComponent(cacheKey);
                // Match upload path with images/ prefix
                return cached.exists ? this.supabase.storage.from('cached-images').getPublicUrl(`images/${encodedCacheKey}.png`).data.publicUrl : null;
            }

            // Encode the cache key to handle special characters
            const encodedCacheKey = encodeURIComponent(cacheKey);
            // Match the path used during upload (images/ prefix)
            const { data } = await this.supabase.storage.from('cached-images').getPublicUrl(`images/${encodedCacheKey}.png`);
            
            // Try to check if image exists, but don't fail if HEAD request has CORS issues
            try {
                const response = await fetch(data.publicUrl, { method: 'HEAD' });
                const exists = response.ok;
                this.headCache.set(cacheKey, { exists, timestamp: Date.now() });
                return exists ? data.publicUrl : null;
            } catch (headError) {
                // If HEAD fails, assume it exists and return the URL
                console.warn('[StorageService] HEAD request failed, returning URL anyway:', cacheKey);
                this.headCache.set(cacheKey, { exists: true, timestamp: Date.now() });
                return data.publicUrl;
            }
        } catch (error) {
            console.error('[StorageService] Error getting cached image:', error);
            return null;
        }
    }

    async cacheImage(cacheKey: string, imageData: string | Blob, metadata: any): Promise<void> {
        try {
            // Ensure bucket exists before trying to upload
            await this.ensureBucketExists();
            let imageBlob: Blob;
            let originalUrl: string = '';
            
            if (typeof imageData === 'string') {
                // If it's a URL, fetch the image
                originalUrl = imageData;
                
                // Check if it's a Together AI URL that needs proxying
                let fetchUrl = imageData;
                if (imageData.includes('api.together.ai/shrt/') || imageData.includes('api.together.xyz/')) {
                    // Use the proxy for Together AI URLs
                    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
                    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
                    fetchUrl = `${supabaseUrl}/functions/v1/proxy-image?url=${encodeURIComponent(imageData)}`;
                    
                    const imageResponse = await fetch(fetchUrl, {
                        headers: {
                            'Authorization': `Bearer ${supabaseAnonKey}`
                        }
                    });
                    
                    if (!imageResponse.ok) {
                        throw new Error(`Failed to fetch image via proxy: ${imageResponse.status} ${imageResponse.statusText}`);
                    }
                    
                    imageBlob = await imageResponse.blob();
                } else {
                    // For other URLs, try direct fetch
                    const imageResponse = await fetch(imageData, {
                        mode: 'cors',
                        credentials: 'omit'
                    });
                    
                    if (!imageResponse.ok) {
                        throw new Error(`Failed to fetch image: ${imageResponse.status} ${imageResponse.statusText}`);
                    }
                    
                    imageBlob = await imageResponse.blob();
                }
            } else if (imageData instanceof Blob) {
                // If it's already a Blob, use it directly
                imageBlob = imageData;
                originalUrl = metadata?.original_url || 'blob';
            } else {
                throw new Error('Invalid image data type');
            }
            
            const encodedCacheKey = encodeURIComponent(cacheKey);
            const fileName = `${encodedCacheKey}.png`;
            const storagePath = `images/${fileName}`;
            
            const { error: uploadError } = await this.supabase.storage
                .from('cached-images')
                .upload(storagePath, imageBlob, { 
                    contentType: 'image/png', 
                    upsert: true 
                });
                
            if (uploadError) {
                throw uploadError;
            }
            
            // Store metadata - ensure we only include valid columns
            const metadataRecord: any = {
                cache_key: cacheKey,
                original_url: originalUrl,
                prompt: metadata.prompt || '',
                style: metadata.style || null,
                user_id: metadata.userId || null,
                generated_at: metadata.generatedAt || new Date(),
                type: metadata.type || null,
                scene_id: metadata.sceneId || null,
                cartridge_id: metadata.cartridgeId || null,
                is_public: metadata.isPublic || false
            };
            
            const { error: metadataError } = await this.supabase
                .from('image_cache_metadata')
                .upsert(metadataRecord, { 
                    onConflict: 'cache_key' 
                });
                
            if (metadataError) {
                console.warn('Failed to store image metadata:', metadataError);
            }
        } catch (error) {
            console.error('[StorageService] Failed to cache image:', error);
            throw error;
        }
    }

    async clearImageCache(userId: string): Promise<void> {
        const { data: metadata, error } = await this.supabase.from('image_cache_metadata').select('cache_key').eq('user_id', userId);
        if (error) throw error;
        if (metadata && metadata.length > 0) {
            const filePaths = metadata.map(item => `${item.cache_key}.png`);
            await this.supabase.storage.from('cached-images').remove(filePaths);
        }
        await this.supabase.from('image_cache_metadata').delete().eq('user_id', userId);
    }
}

export const storageService = new StorageService();
